import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { store } from "./store/store";
import NotificationProvider from "./components/NotificationProvider";
import Sidebar from "./components/Sidebar";
import Topbar from "./components/Navbar";
import Dashboard from "./pages/Dashboard";
import SignUp from "./components/SignUp";
import SignIn from "./components/SignIn";
import ForgotPassword from "./components/ForgotPassword";
import CreateNewPassword from "./components/CreateNewPassword";
import CreatePassword from "./components/CreatePassword";
import BusinessProfile from "./components/BusinessProfile";
import ProfileEmpty from "./pages/ProfileEmpty";
import Bookings from "./pages/Bookings";
import BookingDetails from "./pages/BookingDetails";
import ProfileSettings from "./pages/ProfileSettings";
import Support from "./pages/Support";
import Driver from "./pages/Driver";
import EditDriver from "./pages/EditDriver";
import InviteDriver from "./pages/InviteDriver";
import DriverDetails from "./pages/DriverDetails";
import Reviews from "./pages/Reviews";
import Earnings from "./pages/Earnings";
import Payouts from "./pages/Payouts";
import Services from "./pages/Services";
import Availability from "./pages/Availability";
import ServiceArea from "./pages/ServiceArea";
import TermsPolicies from "./pages/TermsPolicies";
import FlashSales from "./pages/FlashSales";
import ServiceSettings from "./pages/ServiceSettings";
import VerificationInProgress from "./pages/VerificationInProgress";
import { GOOGLE_CLIENT_ID } from "./url";
import ServiceLocation from "./pages/ServiceLocation";
import ServiceDetailsScreen from './pages/ServiceDetailsScreen'

const BASENAME = process.env.NODE_ENV === "production" ? "/provider" : "";

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";
  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }
  return children;
};

const VerifiedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  const profileStatusStr = localStorage.getItem("profileStatus");

  if (profileStatusStr) {
    try {
      const profileStatus = JSON.parse(profileStatusStr);

      if (!profileStatus.isVerified) {
        return <Navigate to="/business-profile" replace />;
      }
    } catch (e) {
      return <Navigate to="/business-profile" replace />;
    }
  } else {
    return <Navigate to="/business-profile" replace />;
  }

  return children;
};

const HomeRedirect = () => {
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  const profileStatusStr = localStorage.getItem("profileStatus");

  if (profileStatusStr) {
    try {
      const profileStatus = JSON.parse(profileStatusStr);

      if (!profileStatus.isVerified) {
        return <Navigate to="/business-profile" replace />;
      }

      return <Navigate to="/dashboard" replace />;
    } catch (e) {
      return <Navigate to="/business-profile" replace />;
    }
  }

  return <Navigate to="/business-profile" replace />;
};

const PublicRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";

  if (!isAuthenticated) {
    return children;
  }

  const profileStatusStr = localStorage.getItem("profileStatus");

  if (profileStatusStr) {
    try {
      const profileStatus = JSON.parse(profileStatusStr);

      if (!profileStatus.isVerified) {
        return <Navigate to="/business-profile" replace />;
      }

      return <Navigate to="/dashboard" replace />;
    } catch (e) {
      return <Navigate to="/business-profile" replace />;
    }
  }

  return <Navigate to="/business-profile" replace />;
};

const PendingVerificationRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  const profileStatusStr = localStorage.getItem("profileStatus");

  if (profileStatusStr) {
    try {
      const profileStatus = JSON.parse(profileStatusStr);

      if (profileStatus.isVerified) {
        return <Navigate to="/dashboard" replace />;
      }
    } catch (e) {}
  }

  return children;
};

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="d-flex">
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      <div className="flex-grow-1">
        <Topbar onMenuClick={toggleSidebar} />
        {children}
      </div>
    </div>
  );
};

function App() {
  return (
    <Provider store={store}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <BrowserRouter basename={BASENAME}>
          <NotificationProvider>
            <Routes>
              <Route path="/" element={<HomeRedirect />} />
              <Route
                path="/signup"
                element={
                  <PublicRoute>
                    <SignUp />
                  </PublicRoute>
                }
              />
              <Route
                path="/signin"
                element={
                  <PublicRoute>
                    <SignIn />
                  </PublicRoute>
                }
              />
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <SignIn />
                  </PublicRoute>
                }
              />
              <Route
                path="/create-password"
                element={
                  <PublicRoute>
                    <CreatePassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/new-password"
                element={
                  <PublicRoute>
                    <CreateNewPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/business-profile"
                element={
                  <PendingVerificationRoute>
                    <BusinessProfile />
                  </PendingVerificationRoute>
                }
              />
              <Route
                path="/verification-pending"
                element={
                  <ProtectedRoute>
                    <VerificationInProgress />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile-empty"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <ProfileEmpty />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <Dashboard />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/bookings"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <Bookings />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/booking-details/:bookingId"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <BookingDetails />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <ProfileSettings />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/support"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <Support />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/terms-policies"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <TermsPolicies />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/reviews"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <Reviews />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/earnings"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <Earnings />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/payouts"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <Payouts />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/driver"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <Driver />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/invite-driver"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <InviteDriver />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/edit-driver"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <EditDriver />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/driver-details"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <DriverDetails />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/services"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <Services />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/availability"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <Availability />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/service-area"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <ServiceArea />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/flash-sales"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <FlashSales />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/service-location/:baseId/service/:serviceId"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <ServiceSettings />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route
                path="/service-location"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <ServiceLocation />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
               <Route
                path="/service-location/:baseId"
                element={
                  <VerifiedRoute>
                    <DashboardLayout>
                      <ServiceDetailsScreen />
                    </DashboardLayout>
                  </VerifiedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </NotificationProvider>
        </BrowserRouter>
      </GoogleOAuthProvider>
    </Provider>
  );
}

export default App;
